/**
 * Pure utility for auto-matching response fields to target columns.
 * No UI, no framework dependencies — reusable across frontend and backend.
 *
 * Type compatibility is a HARD FILTER: incompatible pairs are excluded
 * entirely (e.g. attachment → number). Within compatible/castable pairs,
 * name similarity determines the ranking.
 *
 * Confidence:
 *   high   — name match + compatible/castable type
 *   medium — fuzzy name OR type-only match (compatible/castable, no name)
 *   low    — weak name overlap with castable type
 *
 * Each suggestion carries `alternatives` — all type-compatible candidates
 * for that target, sorted by score desc, for the dropdown.
 */

import { checkCompatibility, normalizeType } from '@streamlain/field-types';

export interface ResponseFieldInfo {
  path: string;
  type: string;
  displayName?: string;
}

export interface TargetColumnInfo {
  id: string;
  name: string;
  type: string;
}

export type MatchConfidence = 'high' | 'medium' | 'low';

export interface MatchCandidate {
  responseField: string;
  responseFieldName?: string;
  score: number;
  confidence: MatchConfidence;
}

export interface AutoMatchSuggestion {
  responseField: string;
  responseFieldName?: string;
  targetColumnId: string;
  targetColumnName: string;
  confidence: MatchConfidence;
  isOverwrite: boolean;
  alternatives: MatchCandidate[];
}

// =============================================================================
// Normalization
// =============================================================================

function normalize(s: string): string {
  return s.toLowerCase().replace(/[._\-\s]/g, '');
}

function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .split(/[._\-\s]+/)
    .filter(Boolean);
}

function levenshteinDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const curr = [i];
    for (let j = 1; j <= n; j++) {
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = curr;
  }
  return prev[n];
}

// =============================================================================
// Scoring
// =============================================================================

/** Name-based score: 0 (no name similarity) to 1 (perfect). */
function nameScore(responseName: string, columnName: string): number {
  const normResponse = normalize(responseName);
  const normColumn = normalize(columnName);

  if (normResponse === normColumn) {
    return responseName.trim() === columnName.trim() ? 1.0 : 0.95;
  }

  const dist = levenshteinDistance(normResponse, normColumn);
  const maxLen = Math.max(normResponse.length, normColumn.length);
  // Length-normalized similarity: short strings need proportionally small dist.
  // "file" vs "title" (dist=2, maxLen=5, sim=0.6) → no auto-match.
  // Long typos like "supplier_adress" → "supplier_address" (sim=0.94) still pass.
  const similarity = 1 - dist / maxLen;
  if (similarity >= 0.7) return similarity;

  if (normResponse.includes(normColumn) || normColumn.includes(normResponse)) {
    const ratio =
      Math.min(normResponse.length, normColumn.length) /
      Math.max(normResponse.length, normColumn.length);
    return 0.5 + ratio * 0.3;
  }

  const responseTokens = tokenize(responseName);
  const columnTokens = tokenize(columnName);
  if (columnTokens.length === 0 || responseTokens.length === 0) return 0;

  let matchedTokens = 0;
  for (const ct of columnTokens) {
    if (responseTokens.some(rt => rt === ct)) matchedTokens++;
  }
  if (matchedTokens === 0) return 0;
  return (matchedTokens / columnTokens.length) * 0.5;
}

/**
 * Auto-map cast quality — stricter than the general `checkCompatibility`.
 *
 * Tier 1 (natural, no data loss):
 *   select/multi-select/number/date/checkbox/relation → text
 *   select ↔ multi-select, text → rich-text
 *   anything ↔ json
 *
 * Tier 2 (lossy, warning):
 *   rich-text → text (markup lost)
 *   attachment → text (only filename)
 *
 * Everything else = excluded (even if checkCompatibility says 'castable',
 * e.g. text→number, text→date, text→select — too error-prone for auto-map).
 */
type CastTier = 'compatible' | 'natural' | 'lossy' | 'excluded';

function autoMapCastTier(sourceType: string, targetType: string): CastTier {
  const compat = checkCompatibility(targetType, sourceType);
  if (compat === 'compatible') return 'compatible';
  if (compat === 'incompatible') return 'excluded';

  // compat === 'castable' — decide tier
  const s = normalizeType(sourceType);
  const t = normalizeType(targetType);

  // Tier 1: natural casts (no data loss)
  if (
    t === 'text' &&
    ['select', 'multi-select', 'number', 'date', 'checkbox'].includes(s)
  )
    return 'natural';
  if (s === 'text' && t === 'rich-text') return 'natural';
  if (
    (s === 'select' && t === 'multi-select') ||
    (s === 'multi-select' && t === 'select')
  )
    return 'natural';
  if (t === 'json' || s === 'json') return 'natural';

  // Tier 2: lossy / risky casts
  if (s === 'rich-text' && t === 'text') return 'lossy'; // markup lost
  if (s === 'attachment' && t === 'text') return 'lossy'; // only filename
  if (s === 'relation' && t === 'text') return 'lossy'; // relation ID, not display name
  if (s === 'text' && t === 'relation') return 'lossy'; // high-risk, needs correct config

  // Everything else castable but not auto-map-safe
  return 'excluded';
}

/**
 * Combined score for a source→target pair.
 *
 * With name match:
 *   compatible → full name score (high)
 *   natural    → name score - 0.05 (high/medium)
 *   lossy      → name score - 0.15 (medium)
 *   excluded   → 0
 *
 * Without name match:
 *   compatible → 0.45 (medium)
 *   natural    → 0.3 (low — visible in dropdown)
 *   lossy      → 0.15 (visible in dropdown, not auto-assigned)
 *   excluded   → 0
 */
function pairScore(field: ResponseFieldInfo, col: TargetColumnInfo): number {
  const tier = autoMapCastTier(field.type, col.type);
  if (tier === 'excluded') return 0;

  const pathS = nameScore(field.path, col.name);
  const dispS = field.displayName ? nameScore(field.displayName, col.name) : 0;
  const best = Math.max(pathS, dispS);

  if (best > 0) {
    switch (tier) {
      case 'compatible':
        return Math.max(best, 0.45);
      case 'natural':
        return Math.max(best - 0.05, 0.4);
      case 'lossy':
        return Math.min(Math.max(best - 0.15, 0.25), 0.75);
    }
  }

  // No name match — type-only
  switch (tier) {
    case 'compatible':
      return 0.45;
    case 'natural':
      return 0.3;
    case 'lossy':
      return 0.15;
  }
}

function scoreToConfidence(score: number): MatchConfidence {
  if (score >= 0.8) return 'high';
  if (score >= 0.4) return 'medium';
  return 'low';
}

// =============================================================================
// Main
// =============================================================================

export function autoMatchFields(
  responseFields: ResponseFieldInfo[],
  targetColumns: TargetColumnInfo[],
  existingMappedColumnIds: Set<string> = new Set()
): AutoMatchSuggestion[] {
  // ── Phase 1: compute type-compatible candidates per target ─────────────

  const candidatesByTarget = new Map<
    string,
    Array<{
      responseField: string;
      responseFieldName?: string;
      score: number;
    }>
  >();

  for (const col of targetColumns) {
    const cands: Array<{
      responseField: string;
      responseFieldName?: string;
      score: number;
    }> = [];
    for (const field of responseFields) {
      const score = pairScore(field, col);
      if (score > 0) {
        cands.push({
          responseField: field.path,
          responseFieldName: field.displayName,
          score,
        });
      }
    }
    cands.sort((a, b) => b.score - a.score);
    if (cands.length > 0) candidatesByTarget.set(col.id, cands);
  }

  // ── Phase 2: greedy best-score-first assignment ────────────────────────
  // Auto-assign only medium+ (≥ 0.4). Low-confidence matches stay
  // available as dropdown alternatives but aren't pre-selected.

  const assignMinScore = 0.4;

  const allPairs: Array<{
    targetId: string;
    responseField: string;
    responseFieldName?: string;
    score: number;
  }> = [];
  for (const [targetId, cands] of candidatesByTarget) {
    for (const c of cands) allPairs.push({ targetId, ...c });
  }
  allPairs.sort((a, b) => b.score - a.score);

  const claimedSources = new Set<string>();
  const claimedTargets = new Set<string>();
  const assignedSource = new Map<string, string>();

  for (const pair of allPairs) {
    if (
      claimedSources.has(pair.responseField) ||
      claimedTargets.has(pair.targetId)
    )
      continue;
    if (pair.score < assignMinScore) continue;
    claimedSources.add(pair.responseField);
    claimedTargets.add(pair.targetId);
    assignedSource.set(pair.targetId, pair.responseField);
  }

  // Phase 2b: for unclaimed targets, try best unclaimed source
  for (const col of targetColumns) {
    if (claimedTargets.has(col.id)) continue;
    const cands = candidatesByTarget.get(col.id);
    if (!cands) continue;
    for (const c of cands) {
      if (claimedSources.has(c.responseField)) continue;
      if (c.score < assignMinScore) continue;
      claimedSources.add(c.responseField);
      claimedTargets.add(col.id);
      assignedSource.set(col.id, c.responseField);
      break;
    }
  }

  // ── Phase 3: build suggestions with alternatives ───────────────────────

  const targetMap = new Map(targetColumns.map(c => [c.id, c]));
  const suggestions: AutoMatchSuggestion[] = [];

  for (const [targetId, responseField] of assignedSource) {
    const col = targetMap.get(targetId)!;
    const cands = candidatesByTarget.get(targetId) ?? [];
    const selected = cands.find(c => c.responseField === responseField);
    if (!selected) continue;

    suggestions.push({
      responseField: selected.responseField,
      responseFieldName: selected.responseFieldName,
      targetColumnId: col.id,
      targetColumnName: col.name,
      confidence: scoreToConfidence(selected.score),
      isOverwrite: existingMappedColumnIds.has(col.id),
      alternatives: cands.map(c => ({
        responseField: c.responseField,
        responseFieldName: c.responseFieldName,
        score: c.score,
        confidence: scoreToConfidence(c.score),
      })),
    });
  }

  const order: Record<MatchConfidence, number> = {
    high: 0,
    medium: 1,
    low: 2,
  };
  suggestions.sort((a, b) => {
    const co = order[a.confidence] - order[b.confidence];
    if (co !== 0) return co;
    return a.targetColumnName.localeCompare(b.targetColumnName);
  });

  return suggestions;
}
